import React, { useEffect, useRef } from "react";

// ═══════════════════════════════════════════════════════════════════════════════
// LIME GREEN CURSOR TRAIL
// ═══════════════════════════════════════════════════════════════════════════════
const GRADIENT_COLORS = ["#BFFF00"];

// Pre-parse RGB values to avoid per-frame hex parsing
const COLORS_RGB = GRADIENT_COLORS.map((h) => ({
  r: parseInt(h.slice(1, 3), 16),
  g: parseInt(h.slice(3, 5), 16),
  b: parseInt(h.slice(5, 7), 16),
}));

export default function CursorTrail() {
  const canvasRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let W = window.innerWidth;
    let H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;

    const TRAIL_LEN = 22;
    const trail = Array.from({ length: TRAIL_LEN }, () => ({
      x: -300,
      y: -300,
    }));

    const particles = [];
    let mouseX = -300;
    let mouseY = -300;
    let lastMouseX = -300;
    let lastMouseY = -300;
    let colorAngle = 0;
    let animId;
    let active = false; // only render once cursor enters

    // Get smoothly interpolated color along the palette
    const getColor = (t) => {
      const wrapped = ((t % GRADIENT_COLORS.length) + GRADIENT_COLORS.length) % GRADIENT_COLORS.length;
      const i = Math.floor(wrapped) % GRADIENT_COLORS.length;
      const j = (i + 1) % GRADIENT_COLORS.length;
      const f = wrapped - Math.floor(wrapped);
      return {
        r: Math.round(COLORS_RGB[i].r + (COLORS_RGB[j].r - COLORS_RGB[i].r) * f),
        g: Math.round(COLORS_RGB[i].g + (COLORS_RGB[j].g - COLORS_RGB[i].g) * f),
        b: Math.round(COLORS_RGB[i].b + (COLORS_RGB[j].b - COLORS_RGB[i].b) * f),
      };
    };

    const onPointerMove = (e) => {
      if (e.pointerType === "touch") return; // trail only for mouse
      const dx = e.clientX - lastMouseX;
      const dy = e.clientY - lastMouseY;
      const speed = Math.sqrt(dx * dx + dy * dy);
      lastMouseX = mouseX;
      lastMouseY = mouseY;
      mouseX = e.clientX;
      mouseY = e.clientY;
      active = true;

      // Spawn physics particles on fast movement
      if (speed > 10) {
        const count = Math.min(5, Math.floor(speed / 9));
        for (let k = 0; k < count; k++) {
          const c = getColor(colorAngle + Math.random() * 2);
          particles.push({
            x: mouseX + (Math.random() - 0.5) * 10,
            y: mouseY + (Math.random() - 0.5) * 10,
            vx: (Math.random() - 0.5) * 3.5,
            vy: -(1.5 + Math.random() * 2.8),
            life: 1.0,
            decay: 0.02 + Math.random() * 0.016,
            r: c.r, g: c.g, b: c.b,
            size: 2.5 + Math.random() * 3.5,
          });
        }
      }
    };

    const animate = () => {
      animId = requestAnimationFrame(animate);
      ctx.clearRect(0, 0, W, H);

      // Advance color angle → smoothly cycles through palette
      colorAngle += 0.018;

      // Update trail - each point lerps toward the one ahead of it
      trail[0].x += (mouseX - trail[0].x) * 0.3;
      trail[0].y += (mouseY - trail[0].y) * 0.3;
      for (let i = 1; i < TRAIL_LEN; i++) {
        trail[i].x += (trail[i - 1].x - trail[i].x) * 0.35;
        trail[i].y += (trail[i - 1].y - trail[i].y) * 0.35;
      }

      // Draw trail (back to front so head is on top)
      if (active) {
        for (let i = TRAIL_LEN - 1; i >= 0; i--) {
          const t = 1 - i / TRAIL_LEN;
          const size = t * 7.5;
          const alpha = t * 0.62;
          // Each point gets its own color offset along the palette
          const c = getColor(colorAngle + i * (GRADIENT_COLORS.length / TRAIL_LEN));
          ctx.beginPath();
          ctx.arc(trail[i].x, trail[i].y, size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${alpha})`;
          ctx.fill();
        }
      }

      // Update & draw physics particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vy += 0.13; // gravity
        p.vx *= 0.975; // air drag
        p.x += p.vx;
        p.y += p.vy;
        p.life -= p.decay;

        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${p.life * 0.82})`;
        ctx.fill();
      }
    };

    animate();
    window.addEventListener("pointermove", onPointerMove);

    const onResize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W;
      canvas.height = H;
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    />
  );
}
